import {
  Block,
  BlockFooter,
  BlockTitle,
  Button,
  Icon,
  Link,
  List,
  ListItem,
  PageContent,
  Sheet,
  Toolbar,
} from "framework7-react";
import { useState } from "react";
import { useSelector } from "react-redux";
import { useBackClose } from "../hooks/use-back-close";
import { requests } from "../constants/requests";
import { useIsPending } from "../hooks/use-is-pending";
import { useToast } from "../hooks/use-toast";
import { selectMe } from "../redux/reducer/app-slice";
import { dismissAssignHint, isAssignHintDismissed } from "../utils/assign-hint";
import {
  getBalanceComponents,
  getBalanceDetail,
  getBalanceLabel,
  getGroupTotals,
  getPersonalSummary,
} from "../utils/balances";
import { countLabel, formatCents } from "../utils/format";
import { getShareMessage } from "../utils/get-share-message";
import { getBalanceInCents } from "../utils/money";
import { PENDING_GROUP_TEXT } from "../utils/outbox";
import OverviewSection from "./overview-section";
import SettlementButton from "./settlement/settlement-button";
import ShareButton from "./share-button";

const balanceColor = {
  positive: "color-green",
  negative: "color-red",
  settled: "",
};

const SKELETON_ROWS = [1, 2, 3];

const PersonalSummary = ({ summary }) => (
  <div className={`group-totals-me balance-${summary.status}`}>
    <span className="group-totals-me-lead">{summary.lead}</span>
    {summary.amount && (
      <>
        {" "}
        <span
          className={`group-totals-me-amount ${balanceColor[summary.status]}`}
        >
          {summary.amount}
        </span>
      </>
    )}
  </div>
);

const AssignHint = ({ onChoose, onDismiss }) => (
  <div className="group-totals-me assign-hint">
    <Link className="assign-hint-choose" onClick={onChoose}>
      Wähle aus, wer du bist, um deinen Stand zu sehen
    </Link>
    <Link
      className="assign-hint-dismiss"
      iconOnly
      aria-label="Hinweis ausblenden"
      onClick={onDismiss}
    >
      <Icon ios="f7:xmark" md="material:close" size={16} />
    </Link>
  </div>
);

const TotalsCard = ({ totals, participantCount, isLoading, children }) => (
  <Block strong inset className="group-totals">
    {isLoading ? (
      <div className="skeleton-text skeleton-effect-wave">
        <div className="group-totals-label">Gesamtausgaben</div>
        <div className="group-totals-amount">100,00 €</div>
      </div>
    ) : (
      <>
        <div className="group-totals-expenses">
          <div className="group-totals-label">Gesamtausgaben</div>
          <div className="group-totals-amount">
            {formatCents(totals.totalExpenses)}
          </div>
        </div>
        <div className="group-totals-meta">
          {totals.allSettled ? (
            <span className="group-totals-settled">
              <Icon
                ios="f7:checkmark_circle_fill"
                md="material:check_circle"
                size={16}
              />
              Alles ausgeglichen
            </span>
          ) : (
            <span>
              {countLabel(totals.expenseCount, "Ausgabe", "Ausgaben")} ·{" "}
              {totals.incomeCount > 0 && (
                <span className="group-totals-income">
                  Einnahmen {formatCents(totals.totalIncome)} ·{" "}
                </span>
              )}
              {countLabel(participantCount, "Person", "Personen")}
            </span>
          )}
        </div>
        {children}
      </>
    )}
  </Block>
);

// Not a component: F7's <List> renders unknown components outside its <ul>.
const renderBalanceRow = (participant, components, isMe) => {
  const label = getBalanceLabel(getBalanceInCents(participant));
  const detail = components ? getBalanceDetail(components) : "";
  return (
    <ListItem
      key={participant.id}
      className={isMe ? "participant-is-me" : undefined}
      title={
        isMe ? (
          <>
            {participant.name} <span className="participant-me">(du)</span>
          </>
        ) : (
          participant.name
        )
      }
      footer={detail || undefined}
    >
      <span
        slot="after"
        className={`participant-balance balance-${label.status}`}
      >
        <span className="participant-balance-verb">{label.verb}</span>
        {label.amount && (
          <>
            {" "}
            <span
              className={`participant-balance-amount ${
                balanceColor[label.status]
              }`}
            >
              {label.amount}
            </span>
          </>
        )}
      </span>
    </ListItem>
  );
};

const ShareSheet = ({ hash, name }) => {
  const { showInfo, showError } = useToast();
  const [sheet, setSheet] = useState(null);
  const releaseBack = useBackClose(Boolean(sheet), () => sheet?.close());

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(hash);
      showInfo("Code kopiert");
    } catch (error) {
      showError("Kopieren nicht möglich");
    }
  };

  return (
    <Sheet
      className="share-sheet"
      style={{ height: "auto" }}
      backdrop
      onSheetOpen={setSheet}
      onSheetClose={releaseBack}
      onSheetClosed={() => setSheet(null)}
    >
      <Toolbar>
        <div className="left"></div>
        <div className="right">
          <Link sheetClose iconOnly aria-label="Schließen">
            <i className="icon icon-close" />
          </Link>
        </div>
      </Toolbar>
      <PageContent>
        <BlockTitle large>Gruppe teilen und einladen</BlockTitle>
        <Block>
          Mit diesem Code kannst du andere in deine Gruppe einladen.
        </Block>
        <Block className="hash-highlight-container">
          <Button tonal className="hash-highlight" onClick={copyCode}>
            <span className="hash-highlight-code">{hash}</span>
            <Icon ios="f7:rectangle_on_rectangle" md="material:content_copy" />
          </Button>
        </Block>
        <Block>
          <ShareButton
            label="Teilen"
            text={getShareMessage(hash, name, window.location.origin)}
            title="Split It – Gruppeneinladung"
          />
        </Block>
      </PageContent>
    </Sheet>
  );
};

const GroupOverview = ({ group, onChooseMe, onOpenSettlement }) => {
  const { participants = [], hash = "", name = "" } = group || {};
  const totals = getGroupTotals(group);
  const components = getBalanceComponents(group);
  const isLoading = useIsPending(requests.loadGroupStatus);
  const me = useSelector(selectMe);
  const summary = getPersonalSummary(me);
  const [dismissedGroupId, setDismissedGroupId] = useState(null);
  const groupId = group?.id;
  const showAssignHint =
    !me &&
    Boolean(onChooseMe) &&
    groupId != null &&
    `${dismissedGroupId}` !== `${groupId}` &&
    !isAssignHintDismissed(groupId);

  const hideAssignHint = () => {
    dismissAssignHint(groupId);
    setDismissedGroupId(groupId);
  };

  return (
    <>
      <TotalsCard
        totals={totals}
        participantCount={participants.length}
        isLoading={isLoading}
      >
        {summary && <PersonalSummary summary={summary} />}
        {showAssignHint && (
          <AssignHint onChoose={onChooseMe} onDismiss={hideAssignHint} />
        )}
      </TotalsCard>
      <OverviewSection title="Salden">
        <List className="participant-list" strong inset dividersIos dividersMd>
          {isLoading
            ? SKELETON_ROWS.map((n) => (
                <ListItem
                  key={n}
                  title="Vorname"
                  after="bekommt 10,00 €"
                  className="skeleton-text skeleton-effect-wave"
                />
              ))
            : participants.map((participant) =>
                renderBalanceRow(
                  participant,
                  components[participant.id],
                  me != null && `${participant.id}` === `${me.id}`,
                ),
              )}
        </List>
        {group?.pendingChanges > 0 && (
          <BlockFooter className="pending-changes-hint">
            {PENDING_GROUP_TEXT}
          </BlockFooter>
        )}
      </OverviewSection>
      {!isLoading && (
        <SettlementButton
          participants={participants}
          meId={me?.id}
          onClick={onOpenSettlement}
        />
      )}
      <Block inset>
        <Button tonal sheetOpen=".share-sheet">
          <Icon ios="f7:person_badge_plus" md="material:person_add" size={20} />
          <span className="overview-invite-label">Einladen</span>
        </Button>
      </Block>
      <ShareSheet hash={hash} name={name} />
    </>
  );
};

export default GroupOverview;
